import { TMasterOccupation } from "@/api/entities/master-occupation.entity";
import { IconPlusCircle } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EMPTY_STR } from "@/constants/string";
import { HiddenOccupationModal } from "@/features/master-data/components/Occupation/HiddenOccupationModal";
import { ShokushuFormModal } from "@/features/master-data/components/Occupation/ShokushuFormModal";
import { useVisible } from "@/hooks/useVisible";
import { background, button, divider, green, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useState } from "react";

type TShokushuFormTableProps = {
  shokushuRows: TMasterOccupation[];
  /** 親のカテゴリID。未保存のカテゴリでは職種を追加できない */
  parentCategoryId?: number;
  parentCategoryName: string;
};

export const ShokushuFormTable: FC<TShokushuFormTableProps> = ({ shokushuRows, parentCategoryId, parentCategoryName }) => {
  const shokushuFormModal = useVisible();
  const [hiddenShokushu, setHiddenShokushu] = useState<TMasterOccupation>();
  const [selectedShokushu, setSelectedShokushu] = useState<TMasterOccupation>();

  const closeShokushuFormModal = () => {
    shokushuFormModal.close();
    setSelectedShokushu(undefined);
  };

  return (
    <>
      <Stack gap={1} flexDirection="column">
        <StyledTable>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>職種</TableCell>
                <TableCell>作業数</TableCell>
                <TableCell>有効／無効</TableCell>
                <TableCell>編集</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shokushuRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ width: "57%" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {row.name || EMPTY_STR.TEXT}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "15%" }}>
                    <Typography color={row.isHidden ? text.tertiary : text.primary} variant="body14">
                      {row.children?.length ?? 0}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: "12%" }}>
                    <Button variant="outline" size="sm" onClick={() => setHiddenShokushu(row)}>
                      {row.isHidden ? "有効化" : "無効化"}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ width: "13%" }}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedShokushu(row);
                        shokushuFormModal.open();
                      }}
                    >
                      編集
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTable>
        {parentCategoryId ? (
          <AddRowButton onClick={shokushuFormModal.open}>
            <IconPlusCircle />
            <Typography variant="cap12">行を追加する</Typography>
          </AddRowButton>
        ) : (
          <Typography variant="cap12" color={text.tertiary}>
            カテゴリを保存すると職種を追加できます。
          </Typography>
        )}
      </Stack>

      {shokushuFormModal.visible && parentCategoryId && (
        <ShokushuFormModal
          isOpen={shokushuFormModal.visible}
          onClose={closeShokushuFormModal}
          selectedShokushu={selectedShokushu}
          parentCategoryId={parentCategoryId}
          parentCategoryName={parentCategoryName}
        />
      )}

      <HiddenOccupationModal hiddenOccupation={hiddenShokushu} setHiddenOccupation={setHiddenShokushu} />
    </>
  );
};

const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: 35vh;
  height: fit-content !important;
  border: 1px solid ${divider.middle};
  background-color: ${background.white};
  table {
    border-collapse: separate;
  }

  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }

  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 4px;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }
`;
